import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, Company, CompanyQuery, api, parseCompanyQuery, safeExternalUrl, serialiseCompanyQuery } from "./api";

type RowJobState = {
  job_id: string | null;
  status: string;
  can_generate: boolean;
  company_version: number | null;
};

const TERMINAL = new Set(["succeeded", "partial", "failed", "cancelled"]);
const ACTIVE = new Set(["queued", "running"]);

function dateText(value?: string): string {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Not recorded" : new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(parsed);
}

function title(value?: string): string {
  return (value ?? "").replace(/[-_]/g, " ");
}

export function enrichmentLabel(status: string, outcome?: string): { label: string; kind: "active" | "ok" | "warn" | "bad" | "idle" } {
  switch (status) {
    case "queued": return { label: "Queued", kind: "active" };
    case "running": return { label: "Enriching…", kind: "active" };
    case "succeeded": return outcome === "no_change" ? { label: "No changes needed", kind: "ok" } : outcome === "review_required" ? { label: "Review suggested fields", kind: "warn" } : { label: "Updated", kind: "ok" };
    case "partial": return { label: "Review partial result", kind: "warn" };
    case "failed": return { label: "Failed", kind: "bad" };
    case "cancelled": return { label: "Cancelled", kind: "idle" };
    default: return { label: "Never enriched", kind: "idle" };
  }
}

export function invalidDateRange(state: CompanyQuery): boolean {
  if (!state.date_from || !state.date_to) return false;
  return state.date_from > state.date_to;
}

function InternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} onClick={event => { if (href.startsWith("/")) { event.preventDefault(); history.pushState({}, "", href); window.dispatchEvent(new PopStateEvent("popstate")); } }}>
      {children}
    </a>
  );
}

function ExternalLink({ href, label, children }: { href: string; label: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
      {children}
    </a>
  );
}

function TagsInline({ label, tags, linkBase }: { label: string; tags?: { name: string; slug?: string }[]; linkBase: string }) {
  if (!tags?.length) return null;
  const shown = tags.slice(0, 2);
  const extra = tags.length - shown.length;
  return (
    <span className="directory-tags" aria-label={label}>
      {shown.map(tag => (
        <InternalLink href={`${linkBase}${encodeURIComponent(tag.slug ?? tag.name)}`} key={tag.slug ?? tag.name}>
          <span className="tag">{tag.name}</span>
        </InternalLink>
      ))}
      {extra > 0 ? <InternalLink href={`${linkBase}${encodeURIComponent(tags[0].slug ?? tags[0].name)}`}><span className="tag tag-more" aria-label={`${extra} more ${label}`}>+{extra}</span></InternalLink> : null}
    </span>
  );
}

function EnrichmentAction({
  company,
  rowState,
  busy,
  onGenerate,
  onRetry,
}: {
  company: Company;
  rowState?: RowJobState;
  busy: boolean;
  onGenerate: () => void;
  onRetry: () => void;
}) {
  const status = rowState?.status ?? company.enrichment?.status ?? "never_run";
  if (!rowState?.can_generate) return null;
  if (ACTIVE.has(status)) {
    return (
      <button className="enrichment-action" disabled aria-busy={status === "running"}>
        <span className="spinner" aria-hidden="true" /> {status === "queued" ? "Queued" : "Enriching…"}
      </button>
    );
  }
  if (status === "failed") {
    return (
      <button className="enrichment-action" onClick={onRetry} disabled={busy}>
        {busy ? "Starting…" : "Retry AI"}
      </button>
    );
  }
  const hasRun = status !== "never_run" && status !== "cancelled";
  return (
    <button className="enrichment-action" onClick={onGenerate} disabled={busy} aria-describedby={company.enrichment?.job_id ? `job-${company.enrichment.job_id}` : undefined}>
      {busy ? "Starting…" : hasRun ? "Refresh with AI" : "Generate with AI"}
    </button>
  );
}

function DirectoryRow({
  company,
  rowState,
  busy,
  onGenerate,
  onRetry,
  showWebsite,
  showSource,
  recentNote,
}: {
  company: Company;
  rowState?: RowJobState;
  busy: boolean;
  onGenerate: () => void;
  onRetry: () => void;
  showWebsite: boolean;
  showSource: boolean;
  recentNote?: string;
}) {
  const official = safeExternalUrl(company.official_url);
  const status = rowState?.status ?? company.enrichment?.status ?? "never_run";
  const statusMeta = recentNote ? { label: "Updated", kind: "ok" as const } : enrichmentLabel(status);
  const source = company.primary_source;
  return (
    <article className="directory-row">
      <div className="directory-row-main">
        <h3 className="directory-row-name">
          <InternalLink href={`/companies/${encodeURIComponent(company.company_id)}`}>{company.name}</InternalLink>
        </h3>
        <div className="directory-row-links">
          {showWebsite ? (
            official
              ? <ExternalLink href={official} label={`${company.name} official website`}><span className="website-link">Website ↗</span></ExternalLink>
              : <span className="website-unknown">Website unknown</span>
          ) : null}
          <span className="directory-row-meta">{company.country || "Global"} · {title(company.funding_stage || company.status || "undisclosed")}</span>
        </div>
        <p className="directory-row-description">{company.description || "Description unavailable."}</p>
      </div>
      <div className="directory-row-tags">
        <TagsInline label="Industry tags" tags={company.industries} linkBase="/companies?industry=" />
        <TagsInline label="Technology tags" tags={company.technologies} linkBase="/companies?tech=" />
      </div>
      <div className="directory-row-source">
        {showSource && source ? (
          <>
            <span className="source-label">
              {source.url ? (
                <ExternalLink href={source.url} label={`Source: ${source.name}`}>{source.name}</ExternalLink>
              ) : (
                source.name
              )}
            </span>
            <small>First seen {dateText(source.first_seen_at ?? company.first_seen_at)}</small>
            {typeof company.source_count === "number" && company.source_count > 1 ? (
              <InternalLink href={`/companies/${encodeURIComponent(company.company_id)}`}>
                <small className="source-count">+{company.source_count - 1} sources</small>
              </InternalLink>
            ) : null}
          </>
        ) : (
          <span className="source-label">Source unavailable</span>
        )}
      </div>
      <div className="directory-row-status">
        <span className={`confidence${statusMeta.kind === "ok" ? " confidence-ok" : ""}`}>
          {company.confidence_score !== undefined ? `${Math.round(company.confidence_score * 100)}% confidence` : "Evidence-led"}
        </span>
        <small>Checked {dateText(company.last_checked_at)}</small>
        <span className={`enrichment-status enrichment-${statusMeta.kind}`} aria-live="polite">{statusMeta.label}</span>
        <EnrichmentAction company={company} rowState={rowState} busy={busy} onGenerate={onGenerate} onRetry={onRetry} />
      </div>
    </article>
  );
}

function Toolbar({
  state,
  draft,
  setDraft,
  onSubmit,
  onChange,
  onClear,
  facets,
  facetsError,
  filterOpen,
  toggleFilters,
  activeFilterCount,
}: {
  state: CompanyQuery;
  draft: string;
  setDraft: (value: string) => void;
  onSubmit: () => void;
  onChange: (key: keyof CompanyQuery, value: string) => void;
  onClear: () => void;
  facets?: Record<string, string[]>;
  facetsError?: Error;
  filterOpen: boolean;
  toggleFilters: () => void;
  activeFilterCount: number;
}) {
  const invalid = invalidDateRange(state);
  const facetsFor = (key: keyof CompanyQuery) => facets?.[key] ?? [];
  return (
    <form className="directory-toolbar" onSubmit={event => { event.preventDefault(); onSubmit(); }} role="search">
      <div className="directory-toolbar-row">
        <label className="search"><span className="sr-only">Search companies</span>
          <input value={draft} onChange={event => setDraft(event.target.value)} placeholder="Search name, offering, or problem" />
        </label>
        <button type="submit">Search</button>
        <label className="sort-label">
          <span className="sr-only">Sort companies</span>
          <select aria-label="Sort companies" value={state.sort ?? "newest"} onChange={event => onChange("sort", event.target.value)}>
            <option value="newest">Newest seen</option>
            <option value="updated">Recently updated</option>
            <option value="confidence">Highest confidence</option>
          </select>
        </label>
        <button type="button" className="filter-toggle" aria-expanded={filterOpen} onClick={toggleFilters}>
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </button>
        {(state.q || activeFilterCount > 0 || state.date_from || state.date_to || state.date_basis) ? (
          <button type="button" className="clear-filters" onClick={onClear}>Clear all filters</button>
        ) : null}
      </div>
      <div className={`directory-filters${filterOpen ? " open" : ""}`}>
        <div className="facet-grid">
          {(["industry", "tech", "stage", "country"] as const).map(key => (
            <label key={key}>{title(key)}
              <select value={state[key] ?? ""} onChange={event => onChange(key, event.target.value)}>
                <option value="">All {title(key)}</option>
                {(facetsFor(key as keyof CompanyQuery)).map(value => <option value={value} key={value}>{value}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div className="date-filters">
          <label>Date basis
            <select value={state.date_basis ?? "first_seen"} onChange={event => onChange("date_basis", event.target.value)}>
              <option value="first_seen">First seen</option>
              <option value="last_checked">Last checked</option>
              <option value="last_enriched">Last enriched</option>
            </select>
          </label>
          <label>From (UTC)
            <input type="date" value={state.date_from ?? ""} onChange={event => onChange("date_from", event.target.value)} />
          </label>
          <label>To (UTC)
            <input type="date" value={state.date_to ?? ""} onChange={event => onChange("date_to", event.target.value)} />
          </label>
          {(state.date_from || state.date_to) ? (
            <button type="button" className="clear-filters" onClick={() => { onChange("date_from", ""); onChange("date_to", ""); }}>Clear dates</button>
          ) : null}
        </div>
        {invalid ? <p className="date-error" role="alert">From must not be later than To.</p> : null}
      </div>
      {facetsError ? <p className="notice error" role="alert">Couldn’t load filter options. {facetsError.message}</p> : null}
    </form>
  );
}

export function DirectoryPage() {
  const [state, setState] = useState<CompanyQuery>(() => parseCompanyQuery(location.search));
  const [draft, setDraft] = useState(() => parseCompanyQuery(location.search).q ?? "");
  const [items, setItems] = useState<Company[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error>();
  const [facets, setFacets] = useState<Record<string, string[]>>();
  const [facetsError, setFacetsError] = useState<Error>();
  const [canGenerate, setCanGenerate] = useState(false);
  const [overlay, setOverlay] = useState<Record<string, RowJobState>>({});
  const [busyJob, setBusyJob] = useState<string>();
  const [recentlyUpdated, setRecentlyUpdated] = useState<Record<string, string>>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [announcement, setAnnouncementRaw] = useState("");
  const announced = useRef<string>("");
  const setAnnouncement = useCallback((message: string) => {
    if (announced.current === message) return;
    announced.current = message;
    setAnnouncementRaw(message);
    setTimeout(() => { announced.current = ""; setAnnouncementRaw(""); }, 4000);
  }, []);
  const stateKey = useMemo(() => serialiseCompanyQuery(state).toString(), [state]);
  const invalid = invalidDateRange(state);

  useEffect(() => {
    const onPop = () => {
      const next = parseCompanyQuery(location.search);
      setState(next);
      setDraft(next.q ?? "");
    };
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => { sessionStorage.setItem("ilma-kina:last-directory", location.href); }, [stateKey]);

  useEffect(() => {
    if (invalid) return;
    let alive = true;
    setInitialLoading(true);
    setError(undefined);
    api.companies(state).then(page => {
      if (!alive) return;
      setItems(page.items);
      setCursor(page.next_cursor ?? null);
      setInitialLoading(false);
    }).catch(caught => { if (!alive) return; setError(caught as Error); setInitialLoading(false); });
    return () => { alive = false; };
  }, [stateKey, invalid]);

  useEffect(() => {
    if (facets) return;
    api.facets().then(setFacets).catch(caught => setFacetsError(caught as Error));
  }, [facets]);

  useEffect(() => {
    api.session().then(session => {
      setCanGenerate(Boolean(session.capabilities?.["company:enrich"]));
    }).catch(() => setCanGenerate(false));
  }, []);

  const refreshOverlay = useCallback(async (ids: string[]) => {
    if (!ids.length) return;
    try {
      const response = await api.enrichmentState(ids);
      setOverlay(current => {
        const next = { ...current };
        for (const item of response.items) next[item.company_id] = { ...item.enrichment, company_version: item.company_version };
        return next;
      });
    } catch { /* operator overlay unavailable; row falls back to public state */ }
  }, []);

  useEffect(() => {
    if (!canGenerate) return;
    const ids = items.map(item => item.company_id);
    refreshOverlay(ids);
  }, [canGenerate, items, refreshOverlay]);

  const loadMore = () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    api.companies(state, cursor).then(page => {
      setItems(current => {
        const known = new Set(current.map(item => item.company_id));
        return [...current, ...page.items.filter(item => !known.has(item.company_id))];
      });
      setCursor(page.next_cursor ?? null);
    }).catch(caught => setError(caught as Error)).finally(() => setLoadingMore(false));
  };

  const refreshFirstPage = useCallback(() => {
    if (invalid) return;
    api.companies(state).then(page => {
      setItems(page.items);
      setCursor(page.next_cursor ?? null);
      setAnnouncement("Directory results updated after enrichment.");
    }).catch(caught => setError(caught as Error));
  }, [state, invalid, setAnnouncement]);

  const pollJob = useCallback(async (jobId: string, companyId: string) => {
    try {
      const job = await api.enrichmentJob(jobId);
      setOverlay(current => {
        const row = current[companyId];
        if (!row) return current;
        return { ...current, [companyId]: { ...row, status: job.status, job_id: jobId } };
      });
      if (TERMINAL.has(job.status)) {
        setRecentlyUpdated(current => ({ ...current, [companyId]: job.status }));
        setTimeout(() => setRecentlyUpdated(current => { const next = { ...current }; delete next[companyId]; return next; }), 8000);
        refreshOverlay([companyId]);
        refreshFirstPage();
      }
      return job;
    } catch {
      return undefined;
    }
  }, [refreshOverlay, refreshFirstPage]);

  useEffect(() => {
    if (!canGenerate) return;
    const active = Object.entries(overlay).filter(([, row]) => row.job_id && ACTIVE.has(row.status));
    if (!active.length) return;
    const timer = setTimeout(() => {
      for (const [companyId, row] of active) pollJob(row.job_id as string, companyId);
    }, 3000 + Math.random() * 1000);
    return () => clearTimeout(timer);
  }, [overlay, canGenerate, pollJob]);

  const startJob = async (company: Company, mode: "fill_missing" | "refresh_ai") => {
    if (busyJob) return;
    setBusyJob(company.company_id);
    try {
      const row = overlay[company.company_id];
      const result = await api.createEnrichmentJob(company.company_id, mode, row?.company_version ?? undefined, crypto.randomUUID());
      setOverlay(current => ({ ...current, [company.company_id]: { ...(current[company.company_id] ?? { job_id: null, status: "never_run", can_generate: true, company_version: null }), job_id: result.job_id, status: result.status } }));
      if (result.status === "queued" || result.status === "running") pollJob(result.job_id, company.company_id);
    } catch (caught) {
      const apiError = caught as ApiError;
      setError(apiError.code === "version_conflict" ? new Error("This company changed recently; refresh the directory and try again.") : apiError);
    } finally {
      setBusyJob(undefined);
    }
  };

  const update = (key: keyof CompanyQuery, value: string) => {
    setState(current => {
      const next = { ...current };
      if (value) next[key] = value; else delete next[key];
      history.pushState({}, "", `/companies?${serialiseCompanyQuery(next)}`);
      return next;
    });
  };

  const onSubmit = () => {
    setState(current => {
      const next = { ...current };
      if (draft.trim()) next.q = draft.trim(); else delete next.q;
      history.pushState({}, "", `/companies?${serialiseCompanyQuery(next)}`);
      return next;
    });
  };

  const onClear = () => {
    const next: CompanyQuery = {};
    setState(next);
    setDraft("");
    history.pushState({}, "", "/companies");
  };

  const activeFilterCount = (["industry", "tech", "stage", "country"] as const).filter(key => state[key]).length + (state.date_from || state.date_to || state.date_basis ? 1 : 0);

  const updateListener = useCallback((event: CustomEvent) => {
    const detail = event.detail as { company_id: string; job_id: string };
    if (detail?.job_id) pollJob(detail.job_id, detail.company_id);
  }, [pollJob]);

  useEffect(() => {
    addEventListener("ilma-kina:enrichment-job", updateListener as EventListener);
    return () => removeEventListener("ilma-kina:enrichment-job", updateListener as EventListener);
  }, [updateListener]);

  return (
    <main id="main-content" className="directory-page">
      <Toolbar
        state={state}
        draft={draft}
        setDraft={setDraft}
        onSubmit={onSubmit}
        onChange={update}
        onClear={onClear}
        facets={facets}
        facetsError={facetsError}
        filterOpen={filterOpen}
        toggleFilters={() => setFilterOpen(open => !open)}
        activeFilterCount={activeFilterCount}
      />
      <div className="directory-results-head">
        <h2 className="sr-only">Company results</h2>
        {!initialLoading && !error ? <span className="result-count">Showing {items.length}{cursor ? "+" : ""} companies</span> : null}
      </div>
      <span className="sr-only" role="status">{announcement}</span>
      {error ? (
        <div className="empty" role="alert">
          <strong>We couldn’t load this data.</strong>
          <p>{error.message}</p>
          <button onClick={() => location.reload()}>Retry</button>
        </div>
      ) : initialLoading ? (
        <div className="directory-skeletons" aria-label="Loading companies">
          {Array.from({ length: 6 }, (_, index) => <div className="directory-row skeleton" key={index} />)}
        </div>
      ) : items.length ? (
        <div className="directory-list">
          {items.map(company => (
            <DirectoryRow
              key={company.company_id}
              company={company}
              rowState={overlay[company.company_id]}
              busy={busyJob === company.company_id}
              onGenerate={() => startJob(company, "fill_missing")}
              onRetry={() => startJob(company, "refresh_ai")}
              showWebsite
              showSource
              recentNote={recentlyUpdated[company.company_id]}
            />
          ))}
          {cursor ? (
            <div className="load-more">
              <button onClick={loadMore} disabled={loadingMore}>{loadingMore ? "Loading…" : "Load more"}</button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="empty">
          <strong>No companies match these filters.</strong>
          <p>Try removing a facet or broadening your search.</p>
          <button onClick={onClear}>Clear filters</button>
        </div>
      )}
    </main>
  );
}
